/**
 * TagEditor — the TAGS row in the asset inspector (ENG-1132).
 *
 * Unreal's Actor Tags panel, in one place for EVERY asset kind: the inspector
 * renders this once from its shared chrome rather than each per-kind section
 * carrying a copy. Nine copies is how folder_path came to work for some kinds
 * and silently do nothing for others.
 *
 * A tag classifies an asset — `furniture`, `walls`, `props`. The immediate use
 * is an environment uploaded as several splats where the character needs to
 * know which one is the sofa, but nothing here is environment-specific: the
 * same row tags a material or an animation.
 *
 * ── Save is immediate, unlike the pose fields above it ──────────────────────
 * Adding a tag is one atomic act with a visible result (a chip appears), not a
 * form being filled in, so a Save button between the two would be a step with
 * nothing in it. Failure is what needs a signal instead, and it gets one: the
 * chip does not appear and the reason is rendered under the field.
 *
 * ── An untaggable kind says so rather than vanishing ────────────────────────
 * `rig` and `ogs` cards are synthesized client-side and have no server row to
 * PATCH. The row still renders, disabled, with the reason — the repo's rule
 * (src/lib/voiceInputState.js's header documents what hiding it costs).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { assetRegistry } from '../../lib/assetRegistry'
import { MAX_TAGS, allTags, isTaggable, readTags, tagError, withTag, withoutTag } from '../../lib/assetTags'

export default function TagEditor({ asset, C, Section }) {
  const tags = readTags(asset)
  const taggable = isTaggable(asset?.kind)
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const inputRef = useRef(null)

  // A new asset selection is a new subject: a half-typed tag and a stale error
  // belong to the card the user just left.
  useEffect(() => { setDraft(''); setError(null) }, [asset?.id])

  // The vocabulary already in use, so a near-miss ("sofas" beside "sofa") is
  // one keystroke away from the real thing instead of a second tag nobody
  // filters on. Datalist rather than a custom menu — it is the native control
  // for exactly this and it needs no keyboard handling of its own.
  const vocabulary = useMemo(
    () => allTags(assetRegistry.list()).filter(t => !tags.includes(t)),
    [asset?.id, tags.join(',')],
  )

  const commit = async (next, { keepFocus = false } = {}) => {
    setBusy(true); setError(null)
    const res = await assetRegistry.setAssetTags(asset.id, next)
    setBusy(false)
    if (!res.ok) setError(res.message ?? res.reason)
    else if (keepFocus) inputRef.current?.focus()
    return res.ok
  }

  const add = async () => {
    const err = tagError(draft, tags)
    if (err) { setError(err); return }
    let next
    try { next = withTag(tags, draft) } catch (e) { setError(e.message); return }
    // Cleared before the request, not after: the field is where the next tag
    // gets typed, and holding the committed text there reads as a failed save.
    setDraft('')
    if (!await commit(next, { keepFocus: true })) setDraft(draft)
  }

  const liveError = draft.trim() === '' ? null : tagError(draft, tags)
  const atCap = tags.length >= MAX_TAGS

  // The inspector's own Section, passed in rather than imported, so this stays
  // a leaf of that module instead of the two importing each other. A bare label
  // beside ASSET and ENVIRONMENT read as a different kind of thing: no
  // disclosure arrow, no divider, and no memory of being collapsed.
  return (
    <Section title="Tags">

      {!taggable && (
        <div data-testid="tags-untaggable" style={{ fontSize: 11, color: C.dim, lineHeight: 1.4 }}>
          {asset?.kind === 'rig' || asset?.kind === 'ogs'
            ? 'This card is assembled from the character, not a stored asset, so it has nowhere to keep a tag.'
            : `"${asset?.kind}" assets cannot be tagged.`}
        </div>
      )}

      {taggable && (
        <>
          <div data-testid="tag-chips" style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
            {tags.length === 0 && (
              <span style={{ fontSize: 11, color: C.dim, fontStyle: 'italic' }}>None</span>
            )}
            {tags.map(t => (
              <span key={t} data-testid={`tag-chip-${t}`} style={chip}>
                {t}
                <button
                  data-testid={`tag-remove-${t}`}
                  onClick={() => commit(withoutTag(tags, t))}
                  disabled={busy}
                  title={`Remove "${t}"`}
                  style={chipX}
                >✕</button>
              </span>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 4 }}>
            <input
              ref={inputRef}
              data-testid="tag-input"
              list="aos-tag-vocabulary"
              value={draft}
              disabled={busy || atCap}
              placeholder={atCap ? `${MAX_TAGS} tags is the limit` : 'add a tag…'}
              onChange={e => { setDraft(e.target.value); setError(null) }}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add() } }}
              style={{ ...input, borderColor: liveError ? C.danger : C.border }}
            />
            <datalist id="aos-tag-vocabulary">
              {vocabulary.map(t => <option key={t} value={t} />)}
            </datalist>
            <button
              data-testid="tag-add"
              onClick={add}
              disabled={busy || atCap || !!liveError || draft.trim() === ''}
              style={addBtn}
            >Add</button>
          </div>

          {/* The rule is the SERVER's (assetTags mirrors it), so showing it as
              you type is the difference between fixing the tag now and watching
              a PATCH come back 400 for a tag that looked fine. */}
          {(liveError || error) && (
            <div data-testid="tag-error" style={{ fontSize: 10, color: C.danger, marginTop: 3 }}>
              {error ?? liveError}
            </div>
          )}
        </>
      )}
    </Section>
  )
}

const chip = {
  display: 'inline-flex', alignItems: 'center', gap: 3,
  padding: '1px 3px 1px 6px', borderRadius: 10,
  background: 'var(--m-accent-soft)', border: '1px solid var(--m-accent)',
  fontFamily: 'var(--m-mono)', fontSize: 10,
}
const chipX = {
  background: 'none', border: 'none', color: 'inherit',
  cursor: 'pointer', padding: '0 2px', fontSize: 9, opacity: 0.7,
}
const input = {
  flex: 1, minWidth: 0, padding: '3px 6px', background: 'var(--m-bg)',
  border: '1px solid', borderRadius: 3, color: 'inherit',
  fontFamily: 'var(--m-mono)', fontSize: 11,
}
const addBtn = {
  background: 'none', border: '1px solid var(--m-line)', borderRadius: 3,
  color: 'inherit', padding: '3px 10px', fontFamily: 'var(--m-font)', fontSize: 11,
  cursor: 'pointer',
}
